'use client';

import { ReactNode, useMemo } from 'react';
import { useUser, useFirestore, useDoc } from '@/firebase';
import { doc } from 'firebase/firestore';
import Header from '@/components/layout/header';
import Footer from '@/components/layout/footer';
import { PrivacySettingsDialog } from '@/components/features/privacy-settings-dialog';
import { MessageNotificationFab } from '@/components/features/message-notification-fab';
import type { Membership } from '@/models/membership';

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const { user, loading: userLoading } = useUser();
  const firestore = useFirestore();

  // Get user profile
  const userProfileRef = useMemo(() => {
    return user ? doc(firestore, 'users', user.uid) : null;
  }, [user, firestore]);

  const { data: userProfile, loading: profileLoading } = useDoc(userProfileRef);

  // Get membership to access profileId (which is used as privacySettingsId)
  const membershipRef = useMemo(() => {
    const membershipId = (userProfile as any)?.membershipId;
    return membershipId ? doc(firestore, 'memberships', membershipId) : null;
  }, [userProfile, firestore]);

  const { data: membership, loading: membershipLoading } = useDoc(membershipRef);
  const membershipData = membership as Membership | undefined;

  // Determine if privacy dialog should be shown
  const shouldShowPrivacyDialog = !userLoading &&
                                  !profileLoading &&
                                  !membershipLoading &&
                                  user &&
                                  userProfile &&
                                  (userProfile as any).membershipId &&
                                  membershipData?.profileId &&
                                  !(userProfile as any).privacySettingsConfigured;

  const handlePrivacyComplete = () => {
    // Dialog will auto-close when privacySettingsConfigured is set to true
    // The useDoc hook will pick up the change and update userProfile
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
      
      {/* Message Notification FAB - shown when user has new messages */}
      {user && (
        <MessageNotificationFab
          userId={user.uid}
          isOfficer={(userProfile as any)?.isOfficer}
          isAdmin={(userProfile as any)?.isAdmin}
        />
      )}
      
      {/* Privacy Settings Dialog - shown once for first-time users */}
      {shouldShowPrivacyDialog && user && membershipData && (
        <PrivacySettingsDialog
          open={true}
          userId={user.uid}
          privacySettingsId={membershipData.profileId}
          onComplete={handlePrivacyComplete}
        />
      )}
    </div>
  );
}
